// Staff email templates (the compose window's sidebar; edited in Settings › Email › Templates).
//
// /emailTemplates/{id}: { name, group: 'office'|'service'|'storage', note, subject, body, labels?, order }
// Until the office saves its own, the starter set below is used. Each Email button names the template
// it opens with (openCompose({ templateId })), so a starter id is kept when the office edits it.
// Text uses {fields} (emailLayoutCore.js MERGE_FIELDS plus the record's own, e.g. {invoice_no}).
// A paragraph that is only {pay_link}, {sign_link} or {portal_link} becomes a button.

import { database } from './initFirebase';
import { ref, get } from 'firebase/database';

export const TEMPLATE_GROUPS = Object.freeze([['office', 'Office'], ['service', 'Service'], ['storage', 'Storage']]);

export const STARTER_TEMPLATES = Object.freeze([
  {
    id: 'invoice', group: 'office', name: 'Invoice ready', note: 'PDF + pay button',
    subject: 'Invoice {invoice_no} from {marina}',
    body: 'Hi {first_name},\n\nYour invoice for {boat} is ready. The balance is {balance}, due {due_date}.\n\n{pay_link}\n\nThe invoice PDF is attached. Please reference {invoice_no} with your payment.',
    labels: { pay_link: 'Pay {balance}' },
  },
  {
    id: 'pastdue', group: 'office', name: 'Past-due balance', note: 'Polite first nudge',
    subject: 'A balance on your account at {marina}',
    body: 'Hi {first_name},\n\nOur records show {balance} is past due on invoice {invoice_no}.\n\n{pay_link}\n\nIf you have already paid, please ignore this note.',
    labels: { pay_link: 'Pay {balance}' },
  },
  {
    id: 'receipt', group: 'office', name: 'Payment receipt', note: 'PDF receipt',
    subject: 'Receipt {receipt_no} from {marina}',
    body: 'Hi {first_name},\n\nThank you for your payment of {amount} received {paid_date}. Your receipt is attached.',
  },
  {
    id: 'purchaseOrder', group: 'office', name: 'Purchase order', note: 'PDF to a vendor',
    subject: 'Purchase order {po_no} from {marina}',
    body: 'Hello,\n\nPlease find attached purchase order {po_no} ({line_count}) for our account {account_no}.\n\nPlease reply to confirm the order and the expected ship date, and put {po_no} on the packing slip and invoice.\n\nThank you.',
  },
  {
    id: 'estimate', group: 'service', name: 'Estimate', note: 'PDF + approve button',
    subject: 'Estimate {estimate_no} for {boat} from {marina}',
    body: 'Hi {first_name},\n\nAttached is estimate {estimate_no} for {boat}, totaling {total}.\n\n{sign_link}\n\nReply to this email or give us a call with any questions, or to go ahead with the work.',
    labels: { sign_link: 'Review and approve' },
  },
  {
    id: 'canvasRepairs', group: 'service', name: 'Canvas repairs to approve', note: 'Approve / decline button',
    subject: 'Repairs to approve for {boat}',
    body: 'Hi {first_name},\n\nWhile {boat}\'s canvas, sails and cushions were out, the loft found {repair_count} that need your OK ({total} if you approve them all).\n\nPlease approve or decline each one:\n\n{sign_link}\n\nReply to this email or give us a call with any questions.',
    labels: { sign_link: 'Approve or decline' },
  },
  {
    id: 'estimateFollowUp', group: 'service', name: 'Estimate follow-up', note: 'A few days after sending',
    subject: 'Estimate {estimate_no} for {boat}',
    body: 'Hi {first_name},\n\nJust checking in on estimate {estimate_no} for {boat}. You can approve it from the portal.\n\n{portal_link}\n\nAny questions, reply here.',
  },
  {
    id: 'launch', group: 'service', name: 'Launch confirmed', note: 'Date + slip',
    subject: '{boat} launches {launch_date}',
    body: 'Hi {first_name},\n\n{boat} is on the schedule to launch {launch_date}, then to slip {slip}.\n\n{portal_link}',
  },
  {
    id: 'parts', group: 'service', name: 'Parts on order', note: 'Delay notice',
    subject: 'Parts update for {boat}',
    body: 'Hi {first_name},\n\nThe {part} for {boat} is on order. We will let you know as soon as it arrives and the job is back on the schedule.',
  },
  {
    id: 'contract', group: 'storage', name: 'Storage contract', note: 'PDF + sign button',
    subject: '{season} storage contract for {boat} from {marina}',
    body: 'Hi {first_name},\n\nAttached is your {season} storage contract for {boat}. Please look it over and sign it by {return_by} to hold your haul and launch weeks.\n\n{sign_link}\n\nReply to this email or give us a call with any questions.',
  },
  {
    id: 'winter', group: 'storage', name: 'Winterization notice', note: 'Seasonal',
    subject: 'Winter storage at {marina}',
    body: 'Hi {first_name},\n\nPlease have {boat} ready to haul by {haul_by}. Your storage contract is ready to sign.\n\n{sign_link}',
  },
  {
    id: 'blank', group: 'storage', name: 'Blank email', note: 'Signature only',
    subject: '', body: 'Hi {first_name},\n\n',
  },
].map((t, i) => ({ ...t, order: i })));

/** The templates to offer: the office's own (with any starter it hasn't replaced), in order. */
export async function loadTemplates() {
  let saved = {};
  try { saved = (await get(ref(database, 'emailTemplates'))).val() || {}; } catch (e) { console.warn('[mail] templates', e); }
  return mergeTemplates(saved);
}

/** Saved templates win; a starter shows until the office saves or deletes one with its id. */
export function mergeTemplates(saved) {
  const out = new Map(STARTER_TEMPLATES.map(t => [t.id, { ...t, starter: true }]));
  for (const [id, t] of Object.entries(saved || {})) {
    if (!t) continue;
    if (t.deleted) { out.delete(id); continue; }
    out.set(id, { ...out.get(id), ...t, id, starter: false });
  }
  return [...out.values()].sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || String(a.name).localeCompare(String(b.name)));
}
